import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

import { PageForRender } from 'render/render.models';
import { DatesService } from 'shared/services/dates/dates.service';


@Injectable()
export class SegmentationService {

  constructor(
    private readonly datesService: DatesService
  ) {}

  public segmentMatches(segment: PageForRender, params: SegmentationParams): boolean {
    if (!isDefined(segment.criteria)) {
      return true;
    }

    const matchParams = {
      userFlags: params.userFlags,
      currentdt: params.currentdt
    };

    return this.everyCriteriaMatches(segment, matchParams);
  }

  public parseUserFlags(flags?: string): Record<string, boolean> {
    const result: Record<string, boolean> = {
      vip: false
    };

    if (!isDefined(flags)) {
      return result;
    }

    const items: string[] = flags.split(',').map((o: string) => o.trim()).filter((o: string) => o.length > 0);

    if (items.length === 0) {
      return result;
    }

    items.forEach((item: string) => {
      if (item.startsWith('-')) {
        result[(item.substring(1))] = false;
      } else {
        result[item] = true;
      }
    });

    return result;
  }

  private everyCriteriaMatches(segment: PageForRender, params: SegmentationParams): boolean {
    const criteria = segment.criteria;
    let hasMatches: boolean = true;

    if (isDefined(params.userFlags)) {
      hasMatches = this.matchesUserFlags(params.userFlags, criteria);
    }

    if (hasMatches && isDefined(params.currentdt)) {
      hasMatches = this.matchesDates(params.currentdt, criteria);
    }

    return hasMatches;
  }

  private matchesUserFlags(flags: any, criteria: any): boolean {
    if (this.isPlainObject(flags)) {
      return true;
    }
    if (this.isPlainObject(criteria)) {
      return true;
    }

    let criteriaFlags: any = criteria.userFlags;

    if (!isDefined(criteriaFlags)) {
      criteriaFlags = this.parseUserFlags(criteriaFlags);
    }

    const flagKeys: string[] = Object.keys(flags);

    const unmatchedFlag: string | undefined = flagKeys.find((key) => {
      const fval = flags[key];
      const cval = criteriaFlags[key];

      return !isDefined(fval) || !isDefined(cval) || cval !== fval;
    });

    return unmatchedFlag === undefined;
  }

  private matchesDates(currentdt: DateTime, criteria: any): boolean {
    if (!isDefined(currentdt)) {
      return true;
    }
    if (this.isPlainObject(criteria)) {
      return true;
    }
    if (!isDefined(criteria.dates)) {
      return false;
    }

    return this.datesService.matchesDates(currentdt, criteria.dates);
  }

  private isPlainObject(o: any): boolean {
    return !isDefined(o) || Object.keys(o).length === 0;
  }

}

export interface SegmentationParams {
  userFlags: string;
  currentdt: DateTime;
}
